import { Component, OnDestroy, OnInit } from "@angular/core";
import { ClipboardTreeNode, TemplateSegmentClipboardService } from "ng/src/app/templates/clipboard/template-segment-clipboard.service";
import { ConfirmationService } from "primeng/api";
import { takeUntil, tap } from "rxjs/operators";
import { merge, Subject } from "rxjs";
import { TemplateSegment, TemplateSegmentTypeId, TemplateTypeId } from "ng/src/app/templates/shared/template.model";
import { Utils } from "ng/src/app/core/utils";

@Component({
	selector: 'template-segment-clipboard',
	templateUrl: 'template-segment-clipboard.component.html',
	styleUrls: ['template-segment-clipboard.component.scss']
})
export class TemplateSegmentClipboardComponent implements OnInit, OnDestroy {
	
	get count() {
		return this.clipboardService.value.length;
	}
	
	get showDeleteButton() {
		return this.clipboardService.inSelectedTemplateContext && !this.clipboardService.hasMultiTemplate;
	}
	
	showingDialog = false;
	segments = new Array<ClipboardTreeNode>();
	errorMap: Record<number, string> = {};
	hasError = false;
	
	private readonly destroySubject = new Subject();
	public readonly destroy$ = this.destroySubject.asObservable();
	public readonly SEGMENT_TYPE = TemplateSegmentTypeId;
	public readonly TEMPLATE_TYPE = TemplateTypeId;
	
	constructor(
		public readonly clipboardService: TemplateSegmentClipboardService,
		private readonly confirmService: ConfirmationService
	) {}
	
	ngOnInit(): void {
		this.segments = this.clipboardService.value;
		
		merge(
			this.clipboardService.onContextChange$,
			this.clipboardService.onChange$.pipe(
				tap(() => {
					this.segments = this.clipboardService.value;
					if (this.showingDialog && this.segments.length == 0)
						this.showingDialog = false;
				})
			)
		)
			.pipe(
				takeUntil(this.destroy$),
				tap(() => this.checkForErrors())
			)
			.subscribe();
	}
	
	ngOnDestroy() {
		this.destroySubject.next();
	}
	
	presentDeleteConfirmation(event: Event) {
		const hasSubTemplate =  this.clipboardService.contextTemplate?.templateType?.id === TemplateTypeId.OPERATIONAL &&
			this.clipboardService.value.some(n => n.data.type.id === TemplateSegmentTypeId.SUB_TEMPLATE);
		
		let message = `Are you sure you want to delete ${this.count} segments from the template?`;
		if (hasSubTemplate) {
			message += ' Any selected sub templates will have their children deleted as well.';
		}
		
		this.confirmService.confirm({
			target: event.target ?? undefined,
			key: 'clipboard-delete',
			header: 'Delete Segments?',
			message,
			rejectButtonStyleClass: 'p-button-secondary',
			acceptButtonStyleClass: 'p-button-danger',
			defaultFocus: 'reject',
			accept: () => this.clipboardService.deleteSelected()
		})
	}
	
	checkForErrors() {
		const templateType = this.clipboardService.contextTemplateType;
		this.hasError = false;
		this.errorMap = Utils.arrayToMap(this.segments, n => {
			const segment = n.data;
			let error = '';
			let sTemplate = this.clipboardService.contextTemplate?.templateType.id;
			
			if (segment.type.id === TemplateSegmentTypeId.SUB_TEMPLATE && this.clipboardService.contextTemplateType !== sTemplate) {
				error = `${templateType === 2 ? 'Sub Template' : 'Section'} type segments cannot be copied into an
                       ${templateType === 2 ? 'Inspection' : 'Operational'} type template.`
			}
			else if (segment.calculate && !this.clipboardService.inSelectedTemplateContext) {
				error = 'A segment using calculations cannot be copied between templates.';
			}
			
			if (error) this.hasError = true;
			this.errorMap[segment.id] = error;
			
			return [segment.id, error];
		})
	}
	
	getSegmentContent(segment: TemplateSegment) {
		return (segment.name ?? segment.text ?? '') + (segment.type.id === TemplateSegmentTypeId.TAG ? (' ' + segment.tagValue!.viewString) : '');
	}
}